import { useId, useContext, useEffect, useCallback, useMemo, useRef } from 'react';
import FormContext from '../../organisms/Form/FormContext';
import { FormFieldProps } from './FormField.types';
import { injectPropsToMatchingFields } from './FormField.utils';
import { FieldValue } from '../../organisms';
import useBuildVariantProps from '../../../modules/theme/useBuildVariantProps';

const usePresenter = (props: FormFieldProps) => {
  const {
    name,
    label,
    children,
    message,
    infoIcon,
    required,
    disabled,
    validate,
    invalid,
    id,
    hasMessage,
  } = props;

  const generatedId = useId();
  const fieldId = id || generatedId;

  const formContext = useContext(FormContext);
  const {
    formState,
    updateFieldValue,
    formErrors,
    updateFieldValidators,
    updateRequiredFields,
  } = formContext || {};

  // Capture whether FormContext was available at initial render.
  // Used to determine if we should inject field props — does not update dynamically.
  const hadFormContext = useRef(!!formContext);

  const value = useMemo(() => formState?.[name], [formState, name]);
  const error = useMemo(() => formErrors?.[name], [formErrors, name]);
  const isInvalid = useMemo(() => !!error || !!invalid, [error, invalid]);

  useEffect(() => {
    updateFieldValidators?.(name, validate);
    return () => updateFieldValidators?.(name, undefined);
  }, [name, validate]);

  useEffect(() => {
    updateRequiredFields?.(name, required);
    return () => updateRequiredFields?.(name, undefined);
  }, [name, required]);

  const handleChange = useCallback(
    (updatedValue: FieldValue) => {
      updateFieldValue?.(name, updatedValue);
    },
    [name, updateFieldValue]
  );

  const messageText = error || message;

  const injected = useMemo(() => (
    injectPropsToMatchingFields(children, {
      name,
      value,
      onValueChange: handleChange,
      disabled,
      invalid: isInvalid,
      required,
      id: fieldId,
    }, hadFormContext.current)
  ), [children, name, fieldId, value, handleChange, disabled, isInvalid, required, hadFormContext.current]);

  const variantProps = useBuildVariantProps(
    'form-field',
    {
      type: isInvalid ? 'error' : 'default'
    },
  );

  return {
    label,
    required,
    infoIcon,
    children: injected,
    messageText,
    fieldId,
    variantProps,
    showMessageContainer: hasMessage || messageText,
    showErrorIcon: isInvalid && messageText,
    showInfoIcon: !isInvalid && messageText,
  };
};

export default usePresenter;
